import React, { useCallback, useEffect, useRef } from 'react';
import { List } from 'antd';
import VirtualList, { ListRef } from 'rc-virtual-list';
import { useTranslation } from 'react-i18next';
import { ListItemStyled, EmptyState, StyledList } from './EPMobileSelectList.styles';

interface EPMobileSelectListProps {
  options: { value: string; label: string }[];
  selectedValue?: string | string[];
  onSelect: (value: string) => void;
  height?: number;
  itemHeight?: number;
  className?: string;
}

const EPMobileSelectList: React.FC<EPMobileSelectListProps> = ({
  options,
  selectedValue,
  onSelect,
  height = 400,
  itemHeight = 32,
  className,
}) => {
  const { t } = useTranslation();
  const virtualListRef = useRef<ListRef>(null);

  useEffect(() => {
    if (selectedValue?.length && virtualListRef.current) {
      const selectedIndex = options.findIndex(option => option.value === selectedValue?.[0]);
      if (selectedIndex !== -1) {
        virtualListRef.current.scrollTo({
          index: selectedIndex,
          align: 'auto',
        });
      }
    }
  }, [selectedValue, options]);

  const handleItemClick = useCallback(
    option => {
      if (!option.disabled) {
        onSelect(option.value);
      }
    },
    [onSelect],
  );

  const renderItem = useCallback(
    option => {
      const isSelected = selectedValue?.includes(option.value);

      return (
        <ListItemStyled $isSelected={isSelected} $itemHeight={itemHeight} onClick={() => handleItemClick(option)}>
          {option.label}
        </ListItemStyled>
      );
    },
    [selectedValue, handleItemClick],
  );

  if (!options.length) {
    return (
      <div style={{ height }} className={className}>
        <EmptyState>{t('No options available')}</EmptyState>
      </div>
    );
  }

  return (
    <StyledList className={className}>
      <VirtualList ref={virtualListRef} data={options} height={height} itemHeight={itemHeight} itemKey="value">
        {option => (
          <List.Item key={option.value} className="list-item">
            {renderItem(option)}
          </List.Item>
        )}
      </VirtualList>
    </StyledList>
  );
};

export default EPMobileSelectList;
